"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Droplets, Hammer, Paintbrush, PaintRoller, ClipboardCheck } from "lucide-react";
const prepSteps = [{
  id: "step-1",
  title: "1. Surface Cleaning",
  icon: Droplets,
  content: "We begin by thoroughly washing and cleaning all surfaces to remove dirt, grime, salt, and loose paint. Living on the Northern Central Coast means dealing with coastal elements, so a pristine canvas is essential for the new coating to adhere properly."
}, {
  id: "step-2",
  title: "2. Sanding & Patching",
  icon: Hammer,
  content: "Every imperfection is meticulously addressed. We scrape away any remaining loose paint, fill holes and cracks with premium fillers, and sand all surfaces smooth to guarantee a flawless, uniform final appearance."
}, {
  id: "step-3",
  title: "3. Priming",
  icon: Paintbrush,
  content: "Bare wood, patched areas, and stubborn stains are treated with high-quality, specialized primers. This crucial step seals the surface, prevents bleed-through, and provides the optimal foundation for the topcoats."
}, {
  id: "step-4",
  title: "4. Precision Coating",
  icon: PaintRoller,
  content: "We apply premium, weather-resistant paints using professional techniques. Our experienced team ensures even coverage, sharp cut-in lines, and a beautiful, durable finish that will protect your property for years."
}, {
  id: "step-5",
  title: "5. Final Inspection",
  icon: ClipboardCheck,
  content: "Before we consider the job complete, we conduct a rigorous walkthrough with you. We check every detail to ensure it meets our exacting standards and, most importantly, your complete satisfaction."
}];
export default function PreparationFinishing() {
  const {
    ref: leftColRef,
    isVisible: isLeftVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: rightColRef,
    isVisible: isRightVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="preparation-finishing" className="bg-background relative overflow-hidden py-20 lg:py-32">
      {}
      <div className="bg-muted/50 absolute top-0 right-0 -mr-20 -mt-20 h-96 w-96 rounded-full blur-3xl" />
      <div className="bg-accent/10 absolute bottom-0 left-0 -ml-20 -mb-20 h-80 w-80 rounded-full blur-3xl" />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16 items-start">
          
          {}
          <div ref={leftColRef} className={`flex flex-col items-start transition-all duration-1000 ease-out ${isLeftVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
            <div className="bg-accent/20 text-primary mb-4 inline-flex items-center rounded-full px-4 py-1.5 text-sm font-semibold tracking-wide">
              Our Process
            </div>
            <h2 className="text-foreground mb-6 text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
              A great finish starts with the <span className="text-primary">prep.</span>
            </h2>
            <p className="text-muted-foreground mb-6 text-lg leading-relaxed">
              At Clint's Coastal Painting and Decorating, we know that a lasting, beautiful paint job is 90% preparation. We never cut corners.
            </p>
            <p className="text-muted-foreground mb-8 text-lg leading-relaxed">
              With over 30 years of experience serving the Northern Central Coast, our meticulous 5-step process ensures your property not only looks stunning but is fully protected against the harsh coastal elements for years to come.
            </p>
            <Button asChild size="lg" className="group">
              <Link to="/ContactPage">
                Get a Free Quote
                <Paintbrush className="ml-2 size-4 transition-transform group-hover:rotate-12" />
              </Link>
            </Button>
          </div>

          {}
          <div ref={rightColRef} className={`rounded-2xl border border-border bg-card p-6 shadow-lg transition-all duration-1000 delay-200 ease-out sm:p-8 ${isRightVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
            <Accordion type="single" collapsible className="w-full" defaultValue="step-1">
              {prepSteps.map((step, i) => {
              const Icon = step.icon;
              return <AccordionItem key={step.id} value={step.id} data-index={i} className="border-border/50">
                    <AccordionTrigger className="text-foreground hover:text-primary text-base sm:text-lg py-5">
                      <div className="flex items-center gap-4">
                        <div className="bg-muted text-primary flex size-10 shrink-0 items-center justify-center rounded-full">
                          <Icon className="size-5" />
                        </div>
                        {step.title}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground pl-14 pb-6 text-base leading-relaxed">
                      {step.content}
                    </AccordionContent>
                  </AccordionItem>;
            })}
            </Accordion>
          </div>

        </div>
      </div>
    </section>;
}